import React from 'react';
import {
  MapContainer, TileLayer, Marker, Popup, ZoomControl,
} from 'react-leaflet';
import L, { LatLngExpression, LatLngTuple } from 'leaflet';
import { PopupDetails, MapPropsRedesign } from '../../../types/search';
import { customPin, customPinActive } from './ui-components/customIcons';
import scrollIntoView, { removeBottomSearchResultFromTheMap } from './ui-components/scrollIntoView';
import { defaultPopupDetails, isProduction, baseURL, onSpaceHandler } from './ui-components/utilities';
import styles from './Map.module.scss';
import { Rating } from '../SearchResult/ui-components';
import { ReactComponent as RefreshIcon } from '../../../../assets/icons/refresh.svg';
import CircleLoader from './ui-components/CircleLoader';
import SearchLocationLoader from './ui-components/SearchLocationLoader';
import { useResultListContext } from 'common/contexts/ResultListContext';
import { screenStore } from 'stores/ScreenStore';
import { switches } from '../ResultsList.utils';

// @ts-ignore
const cityId = window?.hydrateJSON?.cityId;
const EXTENDED_RADIUS_CITIES = ['2400'];

const Map: React.FC<MapPropsRedesign> = ({
  markers,
  fetchData,
  setGeneralData,
  currentMarker,
  getMembershipPrices,
  isLoading,
  mapCenterNo,
  durationOptions,
}) => {
  const mapInstance = React.useRef<L.Map>(null);
  const popupRefs = React.useRef([]);
  const isPopupScroll = React.useRef(false);
  const originalCenter = React.useRef<L.LatLng | null>(null);
  const previousMarker = React.useRef<HTMLElement | null>(null);
  const radiusRef = React.useRef<number | 100>(6.5);

  const [currentPopupDetails, setCurrentPopupDetails] = React.useState<PopupDetails>(defaultPopupDetails);
  const [spaceId, setSpaceId] = React.useState(0);
  const [showLoader, setShowLoader] = React.useState(false);
  const [mapUpdated, setMapUpdated] = React.useState<boolean>(false);
  const [isFirstLoad, setIsFirstLoad] = React.useState<boolean>(true);
  const [isCardHoverTriggered, setIsCardHoverTriggered] = React.useState<boolean>(false);

  const { workspaceType, setActiveFilters, setBudgetValues, setBudgetRange, setHistogramValues } = useResultListContext();
  const {isTablet} = screenStore;
  const isOnlyAirportLoungeChosen = Object.keys(workspaceType).length === 1 && Object.entries(workspaceType)[0].join('') === 'loungeCounttrue';
  // converts degrees to radians
  const deg2rad = (deg:number = 0):number => deg * (Math.PI / 180);

  // calculates distance of 2 points using coordinates (lat,lng)
  const getDistanceFromLatLonInKm = (lat1:number, lon1:number, lat2:number, lon2:number):number => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1); // deg2rad below
    const dLon = deg2rad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
        + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2))
        * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km

    return d;
  };

  const loadMapData = (isResetPage?: boolean):void => {
    const map = mapInstance.current;
    if (map !== null) {
      const center = map.getCenter();
      if (originalCenter.current === null) {
        originalCenter.current = center;
      }
      const ne = map.getBounds().getNorthEast();
      const sw = map.getBounds().getSouthWest();

      const localRadius = EXTENDED_RADIUS_CITIES.includes(cityId) || isOnlyAirportLoungeChosen ? 80 : getDistanceFromLatLonInKm(sw.lat, sw.lng, ne.lat, ne.lng) / 2;
      radius = localRadius;
      radiusRef.current = localRadius;

      if (typeof fetchData === "function") {
        const searchParams = new URLSearchParams(location.search);
        const amenitiesToSetupMapViewState = searchParams.get('amenities') || '';

        const valuesArray = amenitiesToSetupMapViewState.split(',');
        const amenitiesPayload:Record<number, boolean> = {};
        valuesArray.forEach(value => {
          amenitiesPayload[parseInt(value)] = true;
        });
        setActiveFilters(amenitiesPayload);

        const amenitiesFilter = '&amenities='+searchParams.get('amenities')?.replace(/^,+/g, '').replace(/,+/g, ',').replace(/,$/g, '').replace(/^undefined,|,undefined$|,undefined,/g, '').replace(/NaN|NaN,|,NaN$|,NaN,/g, '') || '';

        // duration
        const durationFilters = searchParams.get('duration') || '';

        // sort
        const sortFilter = searchParams.get('sort_by') || '';

        // page 
        const pageFilter = searchParams.get('page') || '';

        // budget 
        // @ts-ignore
        const initialBudget = [Number(window.hydrateJSON?.initial_data.counts.prices.lowest_price), Number(window.hydrateJSON?.initial_data.counts.prices.highest_price)]
        setBudgetRange(initialBudget);
        setBudgetValues([Number(searchParams.get('min_price')) || initialBudget[0], Number(searchParams.get('max_price')) || initialBudget[1]]);
        // @ts-ignore
        setHistogramValues(window.hydrateJSON?.initial_data.counts.prices.prices_histogram);
        const filteredBudget = [Number(searchParams.get('min_price')) || initialBudget[0], Number(searchParams.get('max_price')) || initialBudget[1]];

        fetchData(
          center.lat,
          center.lng,
          localRadius,
          10,
          isResetPage ? 0 : pageFilter ? pageFilter : 0,
          // @ts-ignore
          Object.entries(workspaceType).filter((el) => el[1] == true).map(item => switches[item[0]]),
          amenitiesFilter,
          durationFilters && `&duration=${durationFilters}`,
          filteredBudget,
          sortFilter,
        );
      }
    }
  };

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    setShowLoader(true);
    setCurrentPopupDetails(defaultPopupDetails);
    const fetchPopupData = async (space_id: number | undefined) => {
      const response = await fetch(`${baseURL}ajax/spacedetails?space_id=${space_id}`);
      const json = await response.json();

      const currentActiveDuration = durationOptions.find((duration) => duration.val == 'month')?.isActive ? 'month' : 
            durationOptions.find((duration) => duration.val == 'day')?.isActive ? 'day' : 
            durationOptions.find((duration) => duration.val == 'year')?.isActive ? 'year' : 'month';

      const monthlyPrice = getMembershipPrices(json.memberships, {}, currentActiveDuration);
      const sortedSpaces = monthlyPrice.sort((a:any, b:any) => {
        if (a.name === 'Coworking Space' && b.name !== 'Coworking Space') {
          return -1;
        }
        if (a.name !== 'Coworking Space' && b.name === 'Coworking Space') {
          return 1;
        }

        return 0;
      });

      const newPopupDetails = {
        title: json.name,
        image: json.images ? `${json.images[0].url_no_params}?w=150&h=100&q=90&auto=format,compress&fit=crop&mark=/template/img/wm_icon.png&markscale=5&markalign=center,middle` : '',
        link: json.profile_url_full,
        rating: {
          revs: Number(json.reviews_summary?.review_count),
          star_rating: Number(json.reviews_summary?.overall_rating_data.rating),
        },
        reviews: json.reviews_summary?.review_count ? +json.reviews_summary?.review_count : 0,
        address: json.location?.address_1,
        price: sortedSpaces && sortedSpaces.length > 0 ? sortedSpaces[0].price : null,
        currency: json.currency_code,
      };
      setCurrentPopupDetails(newPopupDetails);
      setShowLoader(false);
    };

    timeoutId = setTimeout(() => fetchPopupData(spaceId), 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [spaceId]);

  const handleMapUpdate = () => {
    setMapUpdated(true);
    setIsCardHoverTriggered(false);
    if (document.querySelector('.leaflet-marker-icon')) {
      setIsFirstLoad(false);
    }
  };
  
  const mapReadyHandler = (): void => {
    setTimeout(() => {
      const map = mapInstance.current;
      if (map !== null) {
        map.on('moveend', () => {
          // if (isTablet) {
          //   removeBottomSearchResultFromTheMap();
          // }
          if (isPopupScroll.current) {
            isPopupScroll.current = false;
          }
          if (originalCenter.current) {
            const newCenter = map.getCenter();
            const distance = getDistanceFromLatLonInKm(originalCenter.current.lat, originalCenter.current.lng, newCenter.lat, newCenter.lng);
            if (distance > radiusRef.current) { // distance (in km) you consider to be 'outside' the original area
              handleMapUpdate();
            }
          }
        });
        map.on('zoomend', () => {
          const currentZoomLevel = mapInstance.current?.getZoom();
          setCurrentZoom(currentZoomLevel);
        });
      }
    }, 100);
  };

  const defaultPosition: LatLngTuple = [1.3644337, 103.7752634];
  let center: LatLngExpression | undefined;
  let cityCenter: LatLngExpression | undefined;
  let csCenter: LatLngExpression | undefined;
  let zoom: number | undefined;
  let radius: number | 100;
  const defaultZoom = 13;
  const zoomInrement = 0.01;
  zoom = defaultZoom;

  const [currentZoom, setCurrentZoom] = React.useState<number | undefined>(defaultZoom);

  // @ts-ignore
  const hJSON = window.hydrateJSON;
  let lat = hJSON.centerCoordinatesJSON[0];
  let lng = hJSON.centerCoordinatesJSON[1];
  if (lat && !Number.isNaN(lat) && lng && !Number.isNaN(lng)) {
    cityCenter = [lat, lng];
  }

  if (typeof (hJSON.locationParams) !== 'undefined') {
    let csLat = hJSON.locationParams.cs_lat;
    let csLng = hJSON.locationParams.cs_lng;
    if (csLat && !Number.isNaN(csLat) && csLng && !Number.isNaN(csLng)) {
      csCenter = [csLat, csLng];
    }
    lat = hJSON.locationParams.lat;
    lng = hJSON.locationParams.lng;
    radius = hJSON.locationParams.radius;
    let n = 2.45;
    if (mapInstance.current) {
      n = mapInstance.current.getSize().x / 256;
    }
    zoom = Math.floor(Math.log2((Math.cos((lat * Math.PI) / 180) * 2 * Math.PI * 6371 * n) / radius));
    zoom = zoom < 6 ? 6 : zoom;
  }

  if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
    center = [lat, lng];
  }

  if (mapCenterNo > 0) {
    zoom = defaultZoom;
    if (mapCenterNo === 1 && cityCenter !== undefined) {
      center = cityCenter;
    } else if (mapCenterNo === 2 && csCenter !== undefined) {
      center = csCenter;
    }
  }

  if (!center) {
    center = defaultPosition;
  }

  React.useEffect(() => {
    // @ts-ignore
    if (document.querySelector('a[aria-label="Close popup"]')) {
      const closeBtn = document.querySelector('a[aria-label="Close popup"]') as HTMLAnchorElement;
      closeBtn?.click();
    }

    if (typeof (currentMarker) !== 'undefined') {
      // const map = mapInstance.current;
      // @ts-ignore
      if (typeof (popupRefs[currentMarker]) !== 'undefined') {
        // @ts-ignore
        // popupRefs[currentMarker]?.openOn(map);
        setSpaceId(Number(currentMarker));
        isPopupScroll.current = true; // set the value of isPopupScroll using .current
  
        // Center the map on the marker
        const marker = markers?.find(marker => marker.id === currentMarker);
        if (marker) {
          // const { position } = marker;
          // map?.flyTo([+position[0], +position[1]], map.getZoom(), {
          //   animate: false,
          // });
          setMapUpdated(false);
          setIsCardHoverTriggered(true);
        }
      }
    }
  }, [currentMarker]);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowLoader(false);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [showLoader]);

  // @ts-ignore
  React.useEffect(() => {
    if (mapCenterNo > 0) {
      const map = mapInstance.current;

      if (map !== null) {
        if (mapCenterNo === 1 && cityCenter !== undefined) {
          center = cityCenter;
        } else if (mapCenterNo === 2 && csCenter !== undefined) {
          center = csCenter;
        }
        zoom = defaultZoom;

        setTimeout(() => {
          // @ts-ignore
          map.setView(center, defaultZoom, {animate: false});
          loadDataOnClick(true);
          setTimeout(() => map.setZoom(map.getZoom() + zoomInrement), 200);
        }, 200);
      }
    }
  }, [mapCenterNo]);

  // CRO_639
  const loadDataOnClick = (wasButtonClicked: boolean = true): void => {
    if (wasButtonClicked) {
      const center = mapInstance.current?.getCenter();
      if (center) {
        originalCenter.current = center;
      }
    }

    if (mapInstance.current !== null) {
      setGeneralData(null);
      loadMapData(true);
      setMapUpdated(false);
    }
  }

  React.useEffect(() => {
    const mapDataTimeout = setTimeout(() => {
      loadMapData();
    }, 100);
  
    const isFirstLoadInterval = setInterval(() => {
      const markerIcon = document.querySelector('.leaflet-marker-icon');
      if (markerIcon) {
        setIsFirstLoad(false);
        clearInterval(isFirstLoadInterval);
      }
    }, 1000);
  
    return () => {
      clearTimeout(mapDataTimeout);
      clearInterval(isFirstLoadInterval);
    };
  }, []);

  // doing hardcode style to prevent changes for old search result's header
  React.useEffect(() => {
    if (isTablet) {
      const header = document.getElementById('react_header');
      const filters = document.getElementById('filters_container');
  
      if (header && filters) {
        header.style.position = 'fixed';
        filters.style.position = 'fixed';
        filters.style.zIndex = '1000';
      }
  
      return () => {
        if (header && filters) {
          header.style.position = '';
          filters.style.position = '';
          filters.style.zIndex = '';
        }
      };
    }
    return;
  }, []);

  const closeDefaultLeafletPopupWindow = () => {
    if (document.querySelector('a[aria-label="Close popup"]')) {
      const closeBtn = document.querySelector('a[aria-label="Close popup"]') as HTMLAnchorElement;
      closeBtn?.click();
    }
  }

  React.useEffect(() => {
    removeBottomSearchResultFromTheMap()
  }, [isTablet])

  return (
    <div className={styles.container}>
      <MapContainer
        ref={mapInstance}
        center={center}
        maxZoom={18}
        scrollWheelZoom
        whenReady={mapReadyHandler}
        zoom={zoom}
        zoomControl={false}
      >
        {/* change condition below */}
        {/* @ts-ignore */}
        {(mapUpdated || isLoading) && currentZoom >= 11 && !isFirstLoad && !isCardHoverTriggered && <button onClick={loadDataOnClick} className={styles.search_this_area}>
          {isLoading ? (
            <SearchLocationLoader />
          ) : (
            <>
              <p>Search this area</p>
              <RefreshIcon />
            </>
          )}
        </button>}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={isProduction ? 'https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png' : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
        />
          {markers?.map(({
            position, id,
          }) => (
            <Marker
              key={id}
              eventHandlers={
                {
                  click: () => {
                    isPopupScroll.current = true;
                    setSpaceId(id);
                    setShowLoader(true);
                    // @ts-ignore
                    scrollIntoView(id, previousMarker);
                    // @ts-ignore
                    if (isTablet || window.CRO_899 == 3) {
                      closeDefaultLeafletPopupWindow();
                    } else {
                      // Center the map on the clicked marker
                      const map = mapInstance.current;
                      map?.flyTo([+position[0], +position[1]], map.getZoom());
                    }
                  },
                }
              }
              icon={currentMarker === id || spaceId === id ? customPinActive : customPin}
              position={new L.LatLng(+position[0], +position[1])}
            >
              <Popup
                key={`popup-${id}`}
                ref={(r) => {
                  // @ts-ignore
                  popupRefs[id] = r;
                }}
              >
                <div className={styles.popup} onClick={() => onSpaceHandler(currentPopupDetails.link)}>
                  {showLoader ? <CircleLoader /> : <img alt="Popup" src={currentPopupDetails.image} />}
                  {currentPopupDetails.price > 0 && (
                    <p className={styles.price}>
                      {`${currentPopupDetails.currency} ${currentPopupDetails.price} / month`}
                    </p>
                  )}
                  <p className={styles.title}>{currentPopupDetails.title}</p>
                  <p className={styles.address}>{currentPopupDetails.address}</p>
                  {currentPopupDetails.rating.revs > 0 ? <Rating {...currentPopupDetails.rating} /> : null}
                </div>
              </Popup>
            </Marker>
          ))}
        <ZoomControl position="topright" />
      </MapContainer>
    </div>
  );
};

export default Map;
